import { Search } from "@mui/icons-material";
import React, { useCallback, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import "react-loading-skeleton/dist/skeleton.css";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import CourseCard from "../../components/CourseCard";
import CourseCardSkeleton from "../../components/skeleton/CourseCardSkeleton";
import { getViewCourses, putToggleBookmark } from "../../services/courseServices";
import { CourseViewContainer } from "../../style/User/ViewCourses";

function ViewCourses() {
  const { categoryId } = useParams();
  const [courses, setCourses] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);

  const ITEMS_PER_PAGE = 12;

  const fetchCourses = useCallback(
    async (pageNum, currentSearch = "") => {
      try {
        setLoading(true);
        const payload = {
          offset: ITEMS_PER_PAGE,
          page: pageNum,
          search: currentSearch,
        };
        const response = await getViewCourses(payload, categoryId);

        if (response.success) {
          setHasMore(response.data.hasMore || false);
          setPage(response.data.nextPage || 0);
          return response.data.courses || [];
        }
        return [];
      } catch (error) {
        console.error("Error fetching courses:", error);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [categoryId]
  );

  const fetchMoreCourses = async () => {
    if (loading || !hasMore) return;

    const newCourses = await fetchCourses(page, searchTerm);
    if (newCourses.length === 0) {
      setHasMore(false);
      return;
    }

    setCourses((prevCourses) => [...prevCourses, ...newCourses]);
  };

  const toggleBookmark = async (courseId, currentSaved) => {
    setCourses((prevCourses) => prevCourses.map((course) => (course.id === courseId ? { ...course, saved: !currentSaved } : course)));

    try {
      const payload = { save: !currentSaved };
      await putToggleBookmark({ payload, courseId });
    } catch (error) {
      toast.error("Unable to Save Course !");

      setCourses((prevCourses) => prevCourses.map((course) => (course.id === courseId ? { ...course, saved: currentSaved } : course)));
    }
  };

  useEffect(() => {
    const loadCourses = async () => {
      setCourses([]);
      setHasMore(true);
      setPage(0);
      setLoading(true);
      setInitialLoading(true);

      try {
        const loadedCourses = await fetchCourses(0, searchTerm);
        setCourses(loadedCourses);
      } catch (error) {
        console.error("Error loading courses:", error);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    const timerId = setTimeout(
      () => {
        loadCourses();
      },
      searchTerm ? 500 : 0
    );

    return () => clearTimeout(timerId);
  }, [searchTerm, categoryId, fetchCourses]);

  const skeletonArray = Array(ITEMS_PER_PAGE).fill(0);

  return (
    <>
      <ToastContainer />
      <CourseViewContainer>
        <div className="search-courses">
          <Search className="search-icon" />
          <input placeholder="Search Course" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>

        <div className="scroll-container">
          <InfiniteScroll
            dataLength={courses.length}
            next={fetchMoreCourses}
            hasMore={hasMore}
            loader={
              <div className="loading-more">
                <div className="course-grid">
                  {Array(4)
                    .fill(0)
                    .map((_, index) => (
                      <CourseCardSkeleton key={`load-more-${index}`} />
                    ))}
                </div>
              </div>
            }
          >
            <div className="course-grid">
              {initialLoading ? (
                skeletonArray.map((_, index) => <CourseCardSkeleton key={`skeleton-${index}`} />)
              ) : courses.length > 0 ? (
                courses.map((course) => (
                  <CourseCard key={course.id} course={course} onToggleBookmark={() => toggleBookmark(course.id, course.saved)} />
                ))
              ) : (
                <div className="notfound-message">No Courses Found</div>
              )}
            </div>
          </InfiniteScroll>
        </div>
      </CourseViewContainer>
    </>
  );
}

export default ViewCourses;
